import { useState, useRef, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import {
  removeFileFromSignRequest,
  setReduxSignRequest,
  setErrors,
  setLoading,
  setUploadedFileNamesDelete,
  setReduxSelectedFile,
  setReduxSelectedParticipant,
} from "redux/appSlice";
import {
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
} from "helpers/methods";
import { getEnvelopeId, getUploadDocumentLimit } from "../../SignRequestService";
import { uploadFiles } from "../../SignRequestHelper";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import strings from "global/constants/StringConstants";
import { hasAccessTo } from "layouts/Config";

export const ACCEPTED_FILE_EXTENSIONS = [
  ".jpeg",
  ".jpg",
  ".png",
  ".gif",
  ".bmp",
  ".tiff",
  ".pdf",
  ".doc",
  ".docx",
  ".txt",
  ".xls",
  ".xlsx",
  ".heic",
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

interface UseDocumentUploadArgs {
  updateTemplate?: boolean;
  isNewTemplate?: boolean;
  /**
   * Skip the upload-document-limit fetch. The signer's documents sheet reuses
   * this hook only to list/select files — it never uploads — so a signer must
   * not call `/secure/document/upload/limit` (a sender-only endpoint).
   */
  skipLimit?: boolean;
}

/**
 * Encapsulates the document-upload orchestration for the mobile Upload step.
 * Mirrors the behavior of the desktop UploadDocuments component but exposes it
 * as a hook so the mobile UI stays presentational. Reuses the same Redux slice,
 * services (getEnvelopeId / getUploadDocumentLimit) and helper (uploadFiles).
 */
export const useDocumentUpload = ({
  updateTemplate,
  isNewTemplate,
  skipLimit = false,
}: UseDocumentUploadArgs) => {
  const dispatch = useAppDispatch();
  const { signRequest, isRequest, uploadedFileNames } = useAppSelector(
    (state: RootState) => state.app,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [docLimit, setDocLimit] = useState(0);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);
  const [maintenanceMsg, setMaintenanceMsg] = useState<string | undefined>();

  const multiDocState = useFeatureState(FeatureKeys.ENVELOPE_MULTI_DOCUMENT);
  const isMultiDocAvailable =
    multiDocState.status === strings.FEATURE_STATUS_AVAILABLE;
  const isMultiDocBlocked =
    signRequest.files.length >= 1 && !isMultiDocAvailable;

  const templateUseState = useFeatureState(FeatureKeys.TEMPLATE_USE);
  const isTemplateUseAvailable =
    templateUseState.status === strings.FEATURE_STATUS_AVAILABLE;
  const templateUseTooltip =
    templateUseState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((templateUseState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : templateUseState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : "";

  const hasMyRequestsAddPermission = useMemo(
    () => hasAccessTo(strings.resources.sign, strings.sendPermission),
    [],
  );
  const hasMyTemplateAddPermission = useMemo(
    () => hasAccessTo(strings.resources.template, strings.addPermission),
    [],
  );
  const hasMyTemplateUpdatePermission = useMemo(
    () => hasAccessTo(strings.resources.template, strings.updatePermission),
    [],
  );

  const hasSendOrUploadAccess = isRequest
    ? hasMyRequestsAddPermission
    : updateTemplate
      ? hasMyTemplateUpdatePermission
      : isNewTemplate
        ? hasMyTemplateAddPermission
        : false;

  useEffect(() => {
    // A signer never uploads, so skip the sender-only limit call entirely.
    if (skipLimit) return;
    (async () => {
      try {
        dispatch(setLoading(true));
        const res = await getUploadDocumentLimit();
        setDocLimit(res);
      } catch (error: any) {
        handleErrorMessage(error);
      } finally {
        dispatch(setLoading(false));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triggerMultiDocGate = () => {
    if (multiDocState.status === "not-in-plan") {
      setUpgradeOpen(true);
    } else {
      setMaintenanceMsg((multiDocState as any).disabledMessage);
      setMaintenanceOpen(true);
    }
  };

  const checkFileSizeAndFormat = (
    file: File,
  ): { isValid: boolean; errorType: string } => {
    const existingFiles = signRequest.files.map(
      (f: any) => f.name || f.fileName,
    );
    if (existingFiles.includes(file.name)) {
      return { isValid: false, errorType: "duplicate" };
    }
    if (uploadedFileNames && uploadedFileNames.includes(file.name)) {
      return { isValid: false, errorType: "duplicate" };
    }
    if (file.size > MAX_FILE_SIZE) {
      return { isValid: false, errorType: "size" };
    }
    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();
    if (!ACCEPTED_FILE_EXTENSIONS.includes(fileExtension)) {
      return { isValid: false, errorType: "format" };
    }
    return { isValid: true, errorType: "" };
  };

  const handleFileUpload = async (files: File[]) => {
    try {
      if (!files || files.length === 0) return;
      let envelopeId = signRequest.id;
      if (signRequest.files.length === 0 && !isTruthy(signRequest.id)) {
        const res = await getEnvelopeId();
        envelopeId = res.message;
      }
      dispatch(setReduxSignRequest({ ...signRequest, id: envelopeId }));
      dispatch(setErrors({}));
      uploadFiles(files, dispatch, isRequest, envelopeId);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const processUploadFiles = (files: File[]) => {
    if (!isMultiDocAvailable && signRequest.files.length + files.length > 1) {
      triggerMultiDocGate();
      return;
    }
    const currentFileCount = signRequest.files.length;
    const remainingSlots = docLimit - currentFileCount;
    if (remainingSlots <= 0) {
      openErrorNotification(
        `Upload limit reached. You can upload a maximum of ${docLimit} documents.`,
      );
      return;
    }
    const filesToProcess = files.slice(0, remainingSlots);
    const exceededFiles = files.slice(remainingSlots);

    const validFiles: File[] = [];
    const duplicateFiles: string[] = [];
    const invalidFiles: string[] = [];
    const fileSizeExceededFiles: string[] = [];

    filesToProcess.forEach((file) => {
      const validation = checkFileSizeAndFormat(file);
      if (validation.isValid) {
        validFiles.push(file);
      } else {
        switch (validation.errorType) {
          case "duplicate":
            duplicateFiles.push(file.name);
            break;
          case "size":
            fileSizeExceededFiles.push(file.name);
            break;
          case "format":
            invalidFiles.push(file.name);
            break;
        }
      }
    });

    let errorMessage = "";
    if (exceededFiles.length > 0) {
      errorMessage += `Only ${remainingSlots} file(s) can be uploaded due to the limit of ${docLimit} documents. ${exceededFiles.length} file(s) were ignored.\n`;
    }
    if (duplicateFiles.length > 0) {
      errorMessage += `File(s) [${duplicateFiles.join(
        ", ",
      )}] already exist. Please try different files.\n`;
    }
    if (invalidFiles.length > 0) {
      errorMessage += `File(s) [${invalidFiles.join(
        ", ",
      )}] have an unsupported file format.\n`;
    }
    if (fileSizeExceededFiles.length > 0) {
      errorMessage += `File(s) [${fileSizeExceededFiles.join(
        ", ",
      )}] exceed the maximum allowed file size of 10MB.`;
    }
    if (errorMessage) {
      openErrorNotification(errorMessage);
    }
    if (validFiles.length > 0) {
      handleFileUpload(validFiles);
    }
  };

  const handleUploadClick = () => {
    if (isMultiDocBlocked) {
      triggerMultiDocGate();
      return;
    }
    if (signRequest.files.length >= docLimit) {
      openErrorNotification(
        `Upload limit reached. You can upload a maximum of ${docLimit} documents.`,
      );
      return;
    }
    if (!hasSendOrUploadAccess) {
      openErrorNotification(
        "Access is denied! Please contact the administrator for assistance.",
      );
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      processUploadFiles(Array.from(files));
    }
    event.target.value = "";
  };

  const handleDeleteFile = (index: number) => {
    const fileToDelete = signRequest.files[index];
    if (fileToDelete) {
      dispatch(
        setUploadedFileNamesDelete(
          fileToDelete.unsignedPdf || fileToDelete.unsignedDoc || "",
        ),
      );
      dispatch(removeFileFromSignRequest(fileToDelete));
    }
  };

  const openTemplateModal = () => {
    dispatch(setReduxSelectedParticipant(null));
    dispatch(setReduxSelectedFile(null));
    setIsTemplateModalOpen(true);
  };
  const closeTemplateModal = () => setIsTemplateModalOpen(false);

  return {
    signRequest,
    isRequest,
    docLimit,
    fileInputRef,
    isMultiDocAvailable,
    isMultiDocBlocked,
    isTemplateUseAvailable,
    templateUseTooltip,
    hasSendOrUploadAccess,
    processUploadFiles,
    handleUploadClick,
    handleFileInputChange,
    handleDeleteFile,
    isTemplateModalOpen,
    openTemplateModal,
    closeTemplateModal,
    upgradeOpen,
    setUpgradeOpen,
    maintenanceOpen,
    setMaintenanceOpen,
    maintenanceMsg,
  };
};
